import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ListChecks,
  Dumbbell,
  Video,
  Users2,
  MessageSquare,
  CheckCircle,
  Target,
} from 'lucide-react';
import { supabase } from '../services/supabase';
import { idsDelBuzonDelClub } from '../lib/buzonDelClub';
import { listaEnCastellano } from '../lib/leerVarias';
import { useAuthStore } from '../store/authStore';
import { hoy } from '../lib/fechaLocal';

// Panel DIRECTOR del Dashboard: tareas que requieren ACCIÓN del admin/director.
// Cada fila enlaza a la página donde se resuelve. (Las tareas por ENTRENADOR
// —PF sin planificar, agendar vídeo-análisis, evaluaciones…— viven en AvisosPanel.)
//   - GYM sin asignar     → alumnos con bono GYM activo sin entrenador GYM.
//   - Vídeo sin analista  → bookings de vídeo-análisis con trainer_id NULL
//                           (el admin asigna el analista en Análisis de vídeo).
//   - Tecnificación       → propuestas de horario individual pendientes.
//   - Clases completas    → sesiones futuras llenas con lista de espera (>0).
//   - Comunicaciones      → mensajes 1:1 sin leer dirigidos al admin.
//
// POR QUÉ NINGUNA CIFRA ES 0 POR DEFECTO
// --------------------------------------
// Este panel es la lista de deberes del director, y su estado vacío no calla:
// dice «Todo al día — sin tareas de administración pendientes». El `try/catch`
// que envolvía las siete lecturas era código muerto —postgrest-js no rechaza
// nunca, devuelve `{ data: null, error }`—, así que un 403 de RLS o una red
// caída no llegaban al `catch`: llegaban como listas vacías, los cinco
// contadores salían a 0 y el panel AFIRMABA que no había nada que hacer. Un
// bono de PF sin entrenador o una propuesta de tecnificación sin sesión se
// quedan esperando a que alguien los mire, y ese alguien acaba de leer que
// puede cerrar la pestaña.
//
// Ahora cada cifra es `number | null`: null = SIN COMPROBAR. Una cifra sin
// comprobar se pinta «—», no suma al total, y bloquea el «Todo al día».

const QUE_GYM = 'los bonos de preparación física';
const QUE_VIDEO = 'las reservas de vídeo-análisis';
const QUE_TECNIF = 'las propuestas de tecnificación';
const QUE_CLASES = 'las clases completas';
const QUE_COMMS = 'las comunicaciones sin responder';

/** null = no se pudo leer. No es 0: un 0 aquí significa «nada que hacer». */
type Cuenta = number | null;

interface AdminTasks {
  gymUnassigned: Cuenta;
  videoNoAnalyst: Cuenta;
  tecnifPending: Cuenta;
  fullClasses: Cuenta;
  unreadComms: Cuenta;
}

const EMPTY: AdminTasks = {
  gymUnassigned: 0,
  videoNoAnalyst: 0,
  tecnifPending: 0,
  fullClasses: 0,
  unreadComms: 0,
};

export default function TareasAdminPanel() {
  const profile = useAuthStore((s) => s.profile);
  const [tasks, setTasks] = useState<AdminTasks>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [fallidos, setFallidos] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const todayStr = hoy();
    const noLeidos: string[] = [];
    const sinComprobar = (que: string): null => {
      noLeidos.push(que);
      return null;
    };

    // 1) GYM sin asignar: alumnos con bono GYM activo sin asignación GYM activa.
    //    Son DOS lecturas para una sola cifra: si falla cualquiera de las dos,
    //    la resta no se puede hacer (sin las asignaciones, TODOS los bonos
    //    parecerían sin entrenador; sin los bonos, ninguno).
    const contarGymSinAsignar = async (): Promise<Cuenta> => {
      const { data: gymCreditsRaw, error: errorCredits } = await supabase
        .from('user_credits')
        .select('user_id')
        .eq('credit_type', 'gym')
        .eq('active', true);
      if (errorCredits) return sinComprobar(QUE_GYM);
      const gymUserIds = Array.from(
        new Set(((gymCreditsRaw ?? []) as { user_id: string }[]).map((c) => c.user_id))
      );
      if (!gymUserIds.length) return 0;
      const { data: gymAssignsRaw, error: errorAssigns } = await supabase
        .from('gym_monitor_assignments')
        .select('user_id')
        .eq('active', true)
        .in('user_id', gymUserIds);
      if (errorAssigns) return sinComprobar(QUE_GYM);
      const assigned = new Set(
        ((gymAssignsRaw ?? []) as { user_id: string }[]).map((a) => a.user_id)
      );
      return gymUserIds.filter((id) => !assigned.has(id)).length;
    };

    // 2) Vídeo-análisis SIN ANALISTA asignado: bookings activos
    //    (pending/scheduled) con trainer_id NULL. El admin asigna el analista
    //    por booking en Análisis de vídeo → Reservas. Un booking sin analista
    //    no lo puede agendar ningún entrenador (RLS trainer_id = auth.uid()).
    //    `count` con `head: true` no trae filas: el error es lo único que
    //    distingue «cero reservas» de «no he podido contarlas».
    const contarVideoSinAnalista = async (): Promise<Cuenta> => {
      const { count, error } = await supabase
        .from('video_analysis_bookings')
        .select('id', { count: 'exact', head: true })
        .is('trainer_id', null)
        .in('status', ['pending', 'scheduled']);
      if (error) return sinComprobar(QUE_VIDEO);
      return count ?? 0;
    };

    // 2c) Tecnificación individual: PROPUESTAS de horario pendientes (motor
    //     común, mig 152). El alumno propuso su día/hora; falta crear la sesión
    //     individual real en Sesiones → Programar (la sub se auto-inscribe por J4).
    const contarTecnifPendiente = async (): Promise<Cuenta> => {
      const { count, error } = await supabase
        .from('user_subscriptions')
        .select('id', { count: 'exact', head: true })
        .eq('class_type', 'individual')
        .eq('status', 'purchased')
        .not('proposed_at', 'is', null);
      if (error) return sinComprobar(QUE_TECNIF);
      return count ?? 0;
    };

    // 3) Clases completas: sesiones futuras programadas y llenas con waitlist.
    const contarClasesCompletas = async (): Promise<Cuenta> => {
      const { data: sessionsRaw, error: errorSessions } = await supabase
        .from('sessions')
        .select('id, current_count, max_students')
        .eq('status', 'scheduled')
        .gte('date', todayStr);
      if (errorSessions) return sinComprobar(QUE_CLASES);
      const fullIds = (
        (sessionsRaw ?? []) as {
          id: string;
          current_count: number | null;
          max_students: number | null;
        }[]
      )
        .filter((s) => (s.current_count ?? 0) >= (s.max_students ?? 0) && (s.max_students ?? 0) > 0)
        .map((s) => s.id);
      if (!fullIds.length) return 0;
      const { data: waitRaw, error: errorWait } = await supabase
        .from('waitlist')
        .select('session_id')
        .in('session_id', fullIds);
      if (errorWait) return sinComprobar(QUE_CLASES);
      const withWait = new Set(
        ((waitRaw ?? []) as { session_id: string }[]).map((w) => w.session_id)
      );
      return fullIds.filter((id) => withWait.has(id)).length;
    };

    // 4) Comunicaciones sin responder: mensajes 1:1 AL BUZÓN DEL CLUB sin
    // leer. Filtrar por `profile.id` dejaba este contador a CERO para
    // cualquier director, siempre: los mensajes al club van dirigidos al
    // admin, no a ellos. Ver `lib/buzonDelClub.ts`.
    const contarComunicaciones = async (): Promise<Cuenta> => {
      if (!profile?.id) return 0;
      // Si el buzón no se pudo leer, el recuento saldría de `[miId]` y diría
      // «no hay nada pendiente» contando solo lo del director. Antes se dejaba
      // en 0 sin preguntar; un 0 en esta fila es «nadie espera respuesta», así
      // que ahora se dice que está sin comprobar.
      const { ids: buzon, error: errorBuzon } = await idsDelBuzonDelClub(profile.id);
      if (errorBuzon) return sinComprobar(QUE_COMMS);
      // ⚠️ Esto NO es «lo que no he leído yo», y por eso sigue con `read` (mig
      // 324): es trabajo pendiente del club —alguien espera respuesta—, no una
      // bandeja personal. `read` significa ahora, con precisión, «su
      // destinatario lo ha visto». Que lo haya abierto un director no responde a
      // la familia, así que esta fila no debe apagarse por eso; el día que se
      // decida que la tarea es «sin RESPONDER», se mira la respuesta y no la
      // lectura.
      const { data: msgsRaw, error: errorMsgs } = await supabase
        .from('messages')
        .select('id')
        .in('receiver_id', buzon)
        .eq('read', false)
        .eq('is_broadcast', false);
      if (errorMsgs) return sinComprobar(QUE_COMMS);
      return ((msgsRaw ?? []) as { id: string }[]).length;
    };

    const [gymUnassigned, videoNoAnalyst, tecnifPending, fullClasses, unreadComms] =
      await Promise.all([
        contarGymSinAsignar(),
        contarVideoSinAnalista(),
        contarTecnifPendiente(),
        contarClasesCompletas(),
        contarComunicaciones(),
      ]);

    setTasks({ gymUnassigned, videoNoAnalyst, tecnifPending, fullClasses, unreadComms });
    // El orden de `noLeidos` depende de cuál conteo termine antes, y la frase
    // la lee una persona: se ordena para que no baile entre recargas.
    setFallidos([...new Set(noLeidos)].sort());
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Las cifras sin comprobar no suman: el total es de lo que SÍ se ha podido
  // contar, y por eso no basta para decir «todo al día».
  const total = Object.values(tasks).reduce<number>((s, v) => s + (v ?? 0), 0);
  const todoComprobado = fallidos.length === 0;

  return (
    <div className="card" data-testid="tareas-admin-panel">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks size={18} className="text-gold-primary" />
        <h3 className="font-oswald text-white text-lg uppercase tracking-wide">
          Tareas pendientes · Administración
        </h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-text-secondary text-sm font-inter py-4">
          <div className="w-3 h-3 border border-gold-primary/40 border-t-gold-primary rounded-full animate-spin" />
          Cargando tareas…
        </div>
      ) : todoComprobado && total === 0 ? (
        <div className="flex items-center gap-2 text-text-secondary text-sm font-inter py-2">
          <CheckCircle size={16} className="text-[#4CAF50]" />
          Todo al día — sin tareas de administración pendientes.
        </div>
      ) : (
        <>
          {fallidos.length > 0 && (
            <div
              className="text-error text-sm font-inter border border-error/30 rounded px-3 py-2 mb-3 flex items-center justify-between gap-3"
              data-testid="tareas-admin-fallidos"
            >
              <span>
                No se ha podido leer {listaEnCastellano(fallidos)}. Lo que sale «—» está sin
                comprobar: no es que no haya nada pendiente.
              </span>
              <button
                className="btn-secondary shrink-0"
                onClick={load}
                data-testid="tareas-admin-reintentar"
              >
                Reintentar
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TaskRow
              testid="tarea-gym-sin-asignar"
              to="/gym/assignments"
              icon={Dumbbell}
              color="#A3E635"
              value={tasks.gymUnassigned}
              label="Bonos Prep. Fís. sin entrenador"
            />
            <TaskRow
              testid="tarea-video-sin-analista"
              to="/video-analisis"
              icon={Video}
              color="#22D3EE"
              value={tasks.videoNoAnalyst}
              label="Vídeo-análisis sin analista asignado"
            />
            <TaskRow
              testid="tarea-tecnif-pendiente"
              to="/agenda?proposals=1"
              icon={Target}
              color="#FFF200"
              value={tasks.tecnifPending}
              label="Propuestas de tecnificación individual"
            />
            <TaskRow
              testid="tarea-clases-completas"
              to="/agenda"
              icon={Users2}
              color="#FF9800"
              value={tasks.fullClasses}
              label="Clases completas con lista de espera"
            />
            <TaskRow
              testid="tarea-comunicaciones"
              to="/comunicaciones"
              icon={MessageSquare}
              color="#2196F3"
              value={tasks.unreadComms}
              label="Comunicaciones sin responder"
            />
          </div>
        </>
      )}
    </div>
  );
}

function TaskRow({
  testid,
  to,
  icon: Icon,
  color,
  value,
  label,
}: {
  testid: string;
  to: string;
  icon: typeof Dumbbell;
  color: string;
  value: Cuenta;
  label: string;
}) {
  // Una fila sin comprobar NO se atenúa: atenuarla es decir «aquí no hay nada».
  const dim = value === 0;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center justify-between bg-bg-elevated border border-bg-border rounded-lg p-3 transition-colors hover:border-gold-primary/50 ${dim ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color }} />
        <span className="text-white font-inter text-sm">{label}</span>
      </div>
      <span
        className={`font-oswald text-2xl leading-none ${value === null ? 'text-error' : 'text-white'}`}
        data-testid={`${testid}-count`}
        title={value === null ? 'Sin comprobar: la consulta ha fallado' : undefined}
      >
        {value === null ? '—' : value}
      </span>
    </Link>
  );
}
